/* Cart-only add-ons. Product-page upsells continue using upsell-simple.js. */
(() => {
  if (customElements.get('cart-addon')) return;
  let pending = false;
  const sectionIds = ['cart-drawer', 'cart-icon-bubble'];

  class CartAddon extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.variants = JSON.parse(this.querySelector('[data-addon-variants]').textContent);
      this.form = this.querySelector('form');
      this.button = this.querySelector('button[type="submit"]');
      this.label = this.querySelector('[data-addon-label]');
      this.defaultLabel = this.label.textContent;
      this.selects = [...this.querySelectorAll('[data-addon-option]')];
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        this.add();
      });
      this.addEventListener('change', () => this.updateVariant());
      this.updateVariant();
    }

    updateVariant() {
      const options = this.selects.map(select => select.value);
      const variant = this.variants.find(item => this.selects.length
        ? item.options.every((value, index) => value === options[index])
        : String(item.id) === this.form.elements.id.value);
      this.variant = variant;
      this.button.disabled = pending || (!this.refreshOnly && !variant?.available);
      this.label.textContent = this.refreshOnly ? 'Refresh cart' : variant?.available ? this.defaultLabel : 'Unavailable';
      this.button.setAttribute('aria-label', this.refreshOnly ? 'Refresh cart' : `Add ${this.querySelector('h3').textContent}`);
      this.form.elements.id.value = variant?.available ? variant.id : '';
      if (variant) {
        this.querySelector('[data-addon-price]').textContent = variant.price;
        this.querySelector('[data-addon-compare]').hidden = !variant.compare;
        this.querySelector('[data-addon-compare-value]').textContent = variant.compare || '';
        const image = this.querySelector('.cart-addon__image');
        if (image && variant.image) {
          image.removeAttribute('srcset');
          image.src = variant.image;
        }
      }
      this.selects.forEach((select, index) => {
        [...select.options].forEach(option => {
          option.disabled = !this.variants.some(item => item.available && item.options.every((value, i) =>
            value === (i === index ? option.value : options[i])));
        });
      });
    }

    showError(message) {
      const error = this.querySelector('.cart-addon__error');
      error.textContent = message;
      error.hidden = false;
    }

    async freshSections() {
      const url = new URL(window.location.href);
      url.searchParams.set('sections', sectionIds.join(','));
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Cart refresh failed');
      return response.json();
    }

    validSections(sections) {
      return sectionIds.every(id => typeof sections?.[id] === 'string' && sections[id].trim()) &&
        new DOMParser().parseFromString(sections['cart-drawer'], 'text/html').querySelector('#CartDrawer');
    }

    async add() {
      if (pending || (!this.refreshOnly && !this.variant?.available)) return;
      pending = true;
      const drawer = this.closest('cart-drawer');
      const scrollTop = drawer.querySelector('cart-drawer-items')?.scrollTop || 0;
      const hadFocus = this.contains(document.activeElement);
      const selections = [...drawer.querySelectorAll('cart-addon')].map(card => ({
        productId: card.dataset.productId, options: card.selects.map(select => select.value)
      }));
      const title = this.querySelector('h3').textContent;
      const status = drawer.querySelector('[data-cart-addon-status]');
      const checkout = drawer.querySelector('#CartDrawer-Checkout');
      const checkoutWasDisabled = checkout?.disabled;
      drawer.querySelectorAll('cart-addon button, cart-addon select').forEach(el => { el.disabled = true; });
      if (checkout) checkout.disabled = true;
      this.setAttribute('aria-busy', 'true');
      this.label.textContent = this.refreshOnly ? 'Refreshing…' : 'Adding…';
      this.querySelector('.cart-addon__error').hidden = true;
      let confirmed = this.addConfirmed || false;
      let submitted = false;
      let rejected = false;
      try {
        let state = {};
        if (!this.refreshOnly) {
          submitted = true;
          const route = window.routes.cart_add_url.replace(/\.js$/, '') + '.js';
          const response = await fetch(route, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: this.variant.id, quantity: 1 }], sections: sectionIds, sections_url: window.location.pathname })
          });
          if (!response.ok) {
            rejected = response.status >= 400 && response.status < 500;
            const error = await response.json().catch(() => ({}));
            throw new Error(error.description || 'This item could not be added. Please try again.');
          }
          confirmed = true;
          this.addConfirmed = true;
          state = await response.json().catch(() => ({}));
        }
        if (!this.validSections(state.sections)) state.sections = await this.freshSections();
        if (!this.validSections(state.sections)) throw new Error('Cart refresh failed');
        await new Promise(resolve => drawer.renderContents(state, {
          preserveInteraction: true, scrollTop, focusAddon: hadFocus, onRendered: () => {
            drawer.querySelectorAll('cart-addon').forEach(card => {
              const previous = selections.find(item => item.productId === card.dataset.productId);
              if (previous && card.variants.some(variant => variant.available && variant.options.every((value, i) => value === previous.options[i]))) {
                card.selects.forEach((select, i) => { select.value = previous.options[i]; });
                card.updateVariant();
              }
            });
            resolve();
          }
        }));
        if (status) status.textContent = confirmed ? `${title} added to your cart.` : 'Cart refreshed. Please check your items.';
      } catch (error) {
        // An accepted or uncertain POST must never be retried automatically.
        this.refreshOnly = confirmed || (submitted && !rejected) || this.refreshOnly;
        this.showError(this.refreshOnly
          ? (confirmed ? 'Item added. Refresh the cart to see the updated total.' : 'We could not confirm the update. Refresh the cart before trying again.')
          : error.message);
        if (status) status.textContent = this.querySelector('.cart-addon__error').textContent;
      } finally {
        pending = false;
        this.removeAttribute('aria-busy');
        drawer.querySelectorAll('cart-addon').forEach(card => {
          card.selects.forEach(select => { select.disabled = false; });
          card.updateVariant();
          if (card.refreshOnly) {
            card.button.disabled = false;
            card.label.textContent = 'Refresh cart';
          }
        });
        // A fresh checkout element already reflects the server's cart state.
        if (checkout?.isConnected) checkout.disabled = checkoutWasDisabled;
        if (hadFocus && !this.isConnected && drawer.classList.contains('active')) {
          const target = drawer.querySelector('cart-addon button') || drawer.querySelector('#CartDrawer-Checkout');
          target?.focus({ preventScroll: true });
        }
      }
    }
  }
  customElements.define('cart-addon', CartAddon);
})();
